import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as api from '../lib/api'

const DEFAULT_LOCALE = 'en'
const LOCALE_STORAGE_KEY = 'tti.locale'

const fallbackValues = {
  'app.title': 'TTI Appraisal',
  'nav.dashboard': 'Dashboard',
  'nav.logout': 'Logout',
  'nav.translations': 'Translations',
  'nav.language': 'Language',
  'footer.prototype': 'TTI Performance Appraisal Platform · Prototype',
  'common.loading': 'Loading...',
  'common.access_denied': 'Access denied.',
  'common.search': 'Search',
  'common.save': 'Save',
  'common.saved': 'Saved',
  'dashboard.no_roles': 'No roles assigned to your account yet. Contact an administrator.',
  'appraisals.session.actions.translate': 'Form Translations',
  'appraisals.session.actions.export': 'Export Data',
  'appraisals.session.actions.add_employees': 'Add Employees',
  'appraisals.session.actions.import_excel': 'Import Excel',
  'translations.title': 'Translation Management',
  'translations.subtitle': 'Edit localized text with English on the left and target language on the right.',
  'translations.locale': 'Target language',
  'translations.domain': 'Domain',
  'translations.key': 'Key',
  'translations.english_source': 'English source',
  'translations.target_text': 'Target text',
  'translations.save_changes': 'Save changes',
  'translations.filter_untranslated': 'Untranslated only',
  'translations.empty': 'No translations found for the selected filters.'
}

const defaultContextValue = {
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  ready: false,
  t: (key, fallback) => fallback || key
}

const I18nContext = createContext(defaultContextValue)

function interpolateValue (template, params) {
  if (!params || typeof template !== 'string') return template
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => {
    const value = params[key]
    return value === undefined || value === null ? '' : String(value)
  })
}

function readSavedLocale () {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY) || DEFAULT_LOCALE
  } catch (e) {
    return DEFAULT_LOCALE
  }
}

export function I18nProvider ({ children }) {
  const [locale, setLocaleState] = useState(readSavedLocale)
  const [values, setValues] = useState(fallbackValues)
  const valuesRef = useRef(fallbackValues)
  const [ready, setReady] = useState(false)

  const setLocale = useCallback((nextLocale) => {
    const normalized = (nextLocale || '').trim() || DEFAULT_LOCALE
    setLocaleState(normalized)
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized)
    } catch (e) {
      // Ignore localStorage failures.
    }
  }, [])

  useEffect(() => {
    let ignore = false
    setReady(false)

    api.getTranslationBundle(locale)
      .then((payload) => {
        if (ignore) return
        const nextValues = {
          ...fallbackValues,
          ...(payload?.values || {})
        }
        valuesRef.current = nextValues
        setValues(nextValues)
      })
      .catch(() => {
        if (ignore) return
        valuesRef.current = fallbackValues
        setValues(fallbackValues)
      })
      .finally(() => {
        if (!ignore) setReady(true)
      })

    return () => {
      ignore = true
    }
  }, [locale])

  const t = useCallback((key, fallback, params) => {
    const value = valuesRef.current[key]
    if (value && String(value).trim()) return interpolateValue(value, params)
    if (fallback !== undefined) return interpolateValue(fallback, params)
    return interpolateValue(key, params)
  }, [])

  const contextValue = useMemo(() => ({
    locale,
    setLocale,
    ready,
    t
  }), [locale, ready, setLocale, t, values])

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
}

export function useI18n () {
  return useContext(I18nContext)
}

export const supportedLocales = [
  { code: 'en', label: 'English' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ms', label: 'Malay' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'zh-Hans', label: 'Simplified Chinese' },
  { code: 'zh-Hant', label: 'Traditional Chinese' }
]
